import { ReactElement, useState, useEffect, useMemo } from 'react'
import { Radio, RadioGroup, ListBox, Button, Select, SelectItem, ListBoxItem, Switch, DatePicker } from '@calabriocloud/beacon-ui'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FormSection, FormHeader } from './layout'
import { ClassifierFieldType, ClassifierField } from './ClassifierFields'
import { convertDateToString, convertDateToDateValue } from '../../util'
import { ErrorMessage, FormikErrors, FormikTouched, useFormikContext } from 'formik'
import { Goal, ContactClassifier, ContactTypeWithClassifiers, contactTypes } from '../../constants'
import { useContactTypeWithClassifiersQuery } from '../api'

interface ClassifierSectionProps {
  values: Goal,
  errors: FormikErrors<Goal>,
  touched: FormikTouched<Goal>,
  handleChange: (e: React.ChangeEvent<Element>) => void,
  handleBlur: (e: React.FocusEvent<Element>) => void,
}

type ClassifierFieldWithId = ClassifierFieldType & { id?: number }
type ClassifierFieldWithOrdinal = ClassifierFieldWithId & { ordinal: number }
export const ClassifierSection = ({ handleBlur, errors, touched }: ClassifierSectionProps): ReactElement => {
  const { t } = useTranslation()
  const formik = useFormikContext<Goal>()
  const [classifier, setClassifier] = useState<{ classifier?: ContactClassifier, classifierValue?: ClassifierFieldType } | undefined>(undefined)
  const [selectedClassifierFieldId, setSelectedClassifierFieldId] = useState<number | undefined>(undefined)
  const { contactStartDate, contactEndDate, contactType, classifiers } = formik.values
  const { classifiers: initialClassifiers } = formik.initialValues
  const [classifierFieldsList, setClassifierFieldList] = useState<ClassifierFieldWithOrdinal[]>(classifiers || [])
  const { isDisabled } = formik.status
  const { data: contactTypeWithClassifiers }: { data: ContactTypeWithClassifiers[] | undefined } = useContactTypeWithClassifiersQuery()
  const [isOpen, setIsOpen] = useState(contactStartDate || contactEndDate ? true : false)
  const moveClassifierUp = (selectedClassifierFieldId: number, classifierFieldsList: ClassifierFieldWithOrdinal[]) => {
    const index = classifierFieldsList.findIndex(item => item.id === selectedClassifierFieldId)
    if (index > 0) {
      const newList = [...classifierFieldsList]
      const temp = newList[index - 1]
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- the classifier array is not sparse
      newList[index - 1] = newList[index]!
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- the classifier array is not sparse
      newList[index] = temp!
      setClassifierFieldList(newList)
    }
  }

  const moveClassifierDown = (selectedClassifierFieldId: number, classifierFieldsList: ClassifierFieldWithOrdinal[]) => {
    const index = classifierFieldsList.findIndex(item => item.id === selectedClassifierFieldId)
    if (index >= 0 && index < classifierFieldsList.length - 1) {
      const newList = [...classifierFieldsList]
      const temp = newList[index + 1]
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- the classifier array is not sparse
      newList[index + 1] = newList[index]!
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- the classifier array is not sparse
      newList[index] = temp!
      setClassifierFieldList(newList)
    }
  }

  useEffect(() => {
    // only update the classifier fields value if it has changed
    const isChanged = JSON.stringify(classifierFieldsList) !== JSON.stringify(initialClassifiers)
    if (isChanged) {
      const updatedClassifierFieldsList: ClassifierFieldWithOrdinal[] = classifierFieldsList.map(item => {
      // remove id to avoid sending to backend
        const { id, ...rest } = item
        return { ...rest, ordinal: classifierFieldsList.findIndex(e => e.id === id), contacts: [] }
      })
      formik.setFieldValue('classifiers', updatedClassifierFieldsList)
    }

  }, [classifierFieldsList])

  const toggleSwitch = (value: boolean) => {
    setIsOpen(value)
    if (!value) {
      formik.setFieldValue('contactStartDate', undefined)
      formik.setFieldValue('contactEndDate', undefined)
    }
  }
  const classifiersOptions = useMemo(() => {
    const availableClassifiers = contactTypeWithClassifiers?.find(contactTypeWithClassifier => contactTypeWithClassifier.id === contactType)?.availableClassifiers || []
    if (contactType && (contactTypes.find(e => e.id === contactType)?.name === 'contact_goal_contact_type_chat' || contactTypes.find(e => e.id === contactType)?.name === 'contact_goal_contact_type_sms')) {
      return availableClassifiers.map(e => e.id !== 7 ? e : { ...e, name: t('externalParty') })
    }
    return availableClassifiers
  }, [contactType, contactTypeWithClassifiers, t])
  return (
    <FormSection>
      <FormHeader title={t('classifiers')} description={t('goalClassifierDescription')} />
      <div className='py-4'>
        <h4 className='mb-1 text-xl'>{t('contactTypev2')}</h4>
        <p className='text-sm mb-4'>{t('goalContactTypeDescription')}</p>
        <Select
          label={t('type')}
          className='w-80'
          data-testid="contact-type-select"
          // isRequired
          id='contactType'
          name='contactType'
          isDisabled={isDisabled}
          selectedKey={contactType}
          items={contactTypeWithClassifiers}
          onBlur={formik.handleBlur}
          onSelectionChange={key => {
            formik.setFieldValue('contactType', key)
          }}
        >
          {item => (<SelectItem key={item.id} id={item.id}>{t(item.name)}</SelectItem>)}
        </Select>
        <ErrorMessage name='contactType'>
          {errorMessage => <div className="text-red-500 text-sm mt-2"> {errorMessage}</div>}
        </ErrorMessage>
      </div>
      <div className='py-4'>
        <div className="flex gap-2 mb-4">
          <ListBox
            aria-label={t('selectClassifiers')}
            className='w-1/4 h-48 overflow-auto'
            renderEmptyState={() => t('noClassifiersSelected')}
            selectionMode='single'
            onSelectionChange={key => {
              if (typeof key !== 'string' && key.size === 1) {
                setSelectedClassifierFieldId(key.values().next().value)
              } else {
                setSelectedClassifierFieldId(undefined)
              }
            }}
            items={classifierFieldsList}
          >
            {item => (
              <ListBoxItem isDisabled={isDisabled}>
                {item.name}
              </ListBoxItem>
            )}
          </ListBox>
          <div className="flex flex-col gap-2 py-12">
            <Button variant="icon" aria-label={t('moveClassifierUp')} isDisabled={selectedClassifierFieldId === undefined || classifierFieldsList.findIndex(item => item.id === selectedClassifierFieldId) === 0 || isDisabled}
              onPress={() => {
                if (selectedClassifierFieldId !== undefined) {
                  moveClassifierUp(selectedClassifierFieldId, classifierFieldsList)
                }
              }}>
              <ChevronUp aria-hidden />
            </Button>
            <Button variant="icon" aria-label={t('moveClassifierDown')} isDisabled={selectedClassifierFieldId === undefined || classifierFieldsList.findIndex(item => item.id === selectedClassifierFieldId) === classifierFieldsList.length - 1 || isDisabled}
              onPress={() => {
                if (selectedClassifierFieldId !== undefined) {
                  moveClassifierDown(selectedClassifierFieldId, classifierFieldsList)
                }
              }}>
              <ChevronDown aria-hidden />
            </Button>
          </div>
        </div>
        <Select
          className='w-80 mb-4'
          label={t('classifier')}
          id='classifier'
          name='classifier'
          isDisabled={isDisabled || !contactType}
          onBlur={handleBlur}
          onSelectionChange={key => {
            setClassifier({ classifier: classifiersOptions?.find(item => item.id === key), classifierValue: undefined })
          }}
          items={classifiersOptions || []}
        >
          {item => (
            <SelectItem
              key={item.id}
              id={item.id}
            >
              {t(item.name)}
            </SelectItem>
          )}
        </Select>
        <ErrorMessage name='classifiers'>
          {errorMessage => <div className="text-red-500 text-sm mt-2"> {errorMessage}</div>}
        </ErrorMessage>
        <ClassifierField
          classifier={classifier?.classifier}
          field={classifier?.classifierValue}
          onChange={value => {
            setClassifier({ ...classifier, classifierValue: value })
          }} />
        <div className='flex gap-2 mb-4'>
          <Button isDisabled={!classifier?.classifierValue || isDisabled} onPress={() => {
            if (classifier?.classifierValue) {
              const id = classifierFieldsList[classifierFieldsList.length - 1]?.id ?? 0
              setClassifierFieldList([...classifierFieldsList, { ...classifier.classifierValue, id: id + 1, ordinal: classifierFieldsList.findIndex(e => e.id === id) }])
              setClassifier({ classifier: undefined, classifierValue: undefined })
            }
          }}>{t('add')}</Button>
          <Button isDisabled={!selectedClassifierFieldId || isDisabled} variant='destructive' onPress={() => {
            if (selectedClassifierFieldId) {
              setClassifierFieldList(classifierFieldsList.filter(item => item.id !== selectedClassifierFieldId))
            }
          }}>{t('removeSelected')}</Button>
        </div>
        <span className='text-sm mt-4'>{t('anyOrAllGuidance')}</span>
        <RadioGroup name='classifierOperator' isRequired defaultValue="any" onChange={value => formik.setFieldValue('classifierOperator', value)}>
          <Radio data-testid='match-any-option' isDisabled={isDisabled} value="any">{t('matchAnyClassifiers')}</Radio>
          <Radio data-testid='match-all-option' isDisabled={isDisabled} value="all">{t('matchAllClassifiers')}</Radio>
        </RadioGroup>
      </div>
      <div className='py-4'>
        <h4 className='text-xl'>{t('useContactDatev2')}</h4>
        <Switch
          isSelected={isOpen}
          isDisabled={isDisabled}
          onChange={value => toggleSwitch(value)}
          className='mt-4'
        >
          {t('assignContactDescription')}
        </Switch>
        {isOpen && (
          <div className='flex flex-row gap-4 mt-4 w-full'>
            <DatePicker
              label={t('contactStartDatev2')}
              name={'contactStartDate'}
              data-testid='contact-start-date-input'
              isDisabled={isDisabled}
              errorMessage={errors.contactStartDate}
              isInvalid={errors.contactStartDate !== undefined && touched.contactStartDate}
              onChange={value => formik.setFieldValue('contactStartDate', convertDateToString(value))}
              value={convertDateToDateValue(contactStartDate)}
            />
            <DatePicker
              label={t('contactEndDatev2')}
              name={'contactEndDate'}
              data-testid='contact-end-date-input'
              isDisabled={isDisabled}
              errorMessage={errors.contactEndDate}
              isInvalid={errors.contactEndDate !== undefined && touched.contactEndDate}
              onChange={value => formik.setFieldValue('contactEndDate', convertDateToString(value))}
              value={convertDateToDateValue(contactEndDate)}
            />
          </div>
        )}
      </div>
    </FormSection>
  )
}

